import { useCallback, useState } from "react";
import { cancelETaxSubmission } from "../../services";
import { WithdrawTarget } from "../../components/ETax/CancelSubmissionModal";

/**
 * Shared state for withdrawing a document from the filing agent: which bill is being
 * confirmed, whether the request is in flight, and reloading the list afterwards.
 *
 * The detail list and the batch-item list both offer this action and both must repaint
 * after it — a withdrawn document that still reads "รอตัวแทนนำส่งยืนยัน" on screen is
 * exactly the misreading the whole feature exists to prevent — so the reload is the
 * caller's own fetch, passed in.
 *
 * Nothing here reports failures. The API's error toast carries KTB's own reason for a
 * refusal, and restating it in weaker words would only bury it.
 */
export function useEtaxWithdraw(reload: () => void) {
  const [target, setTarget] = useState<WithdrawTarget | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const close = useCallback(() => {
    if (!submitting) setTarget(null);
  }, [submitting]);

  const confirm = useCallback(async () => {
    if (!target || submitting) return;

    setSubmitting(true);
    try {
      await cancelETaxSubmission(target.transactionUuid);
      setTarget(null);
      reload();
    } catch {
      // The dialog stays open on a refusal, so the bill it names is still on screen while
      // the operator reads why it was refused.
    } finally {
      setSubmitting(false);
    }
  }, [target, submitting, reload]);

  return { withdrawTarget: target, withdrawing: submitting, askWithdraw: setTarget, closeWithdraw: close, confirmWithdraw: confirm };
}
